import { createContext } from "react";
import { createStore } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import {
  ANALYTICS_SLICE_DEFAULTS,
  AnalyticsSlice,
  analyticsSlice,
  AnalyticsSliceState
} from "./slices/analyticsSlice";
import {
  API_DATA_SLICE_DEFAULTS,
  ApiDataSlice,
  apiDataSlice,
  ApiDataSliceState
} from "./slices/apiDataSlice";
import {
  MEDIA_ELEMENT_SLICE_DEFAULTS,
  MediaElementSlice,
  mediaElementSlice,
  MediaElementSliceState
} from "./slices/mediaElementSlice";
import {
  TIME_SLICE_DEFAULTS,
  TimeSlice,
  timeSlice,
  TimeSliceState
} from "./slices/timeSlice";
import {
  TRACKS_SLICE_DEFAULTS,
  TracksSlice,
  tracksSlice,
  TracksSliceState
} from "./slices/tracksSlice";
import {
  UI_SLICE_DEFAULTS,
  UiSlice,
  uiSlice,
  UiSliceState
} from "./slices/uiSlice";
import {
  USER_DATA_SLICE_DEFAULTS,
  UserDataSlice,
  userDataSlice,
  UserDataSliceState
} from "./slices/userDataSlice";
import { WithSelectors } from "./createSelectors";
import { StoreMiddlewares } from "./storeTypes";

export type PlayerStoreProperties = AnalyticsSlice &
  ApiDataSlice &
  MediaElementSlice &
  UiSlice &
  UserDataSlice &
  TimeSlice &
  TracksSlice;

export type PlayerState = AnalyticsSliceState &
  ApiDataSliceState &
  MediaElementSliceState &
  UserDataSliceState &
  UiSliceState &
  TimeSliceState &
  TracksSliceState;

export const PLAYER_STORE_DEFAULTS = Object.assign(
  {},
  ANALYTICS_SLICE_DEFAULTS,
  API_DATA_SLICE_DEFAULTS,
  MEDIA_ELEMENT_SLICE_DEFAULTS,
  UI_SLICE_DEFAULTS,
  USER_DATA_SLICE_DEFAULTS,
  TIME_SLICE_DEFAULTS,
  TRACKS_SLICE_DEFAULTS
);

type PlayerStore = ReturnType<
  typeof createStore<PlayerStoreProperties, StoreMiddlewares>
>;

export const createPlayerStore = (embedId?: string): PlayerStore => {
  return createStore<PlayerStoreProperties>()(
    devtools(
      immer(
        subscribeWithSelector((...a) => ({
          ...analyticsSlice(...a),
          ...apiDataSlice(...a),
          ...mediaElementSlice(...a),
          ...uiSlice(...a),
          ...userDataSlice(...a),
          ...timeSlice(...a),
          ...tracksSlice(...a)
        }))
      ),
      {
        enabled: process.env.NODE_ENV === "development",
        name: `PlayerStore#${embedId}`
      }
    )
  );
};

export type PlayerStoreWithSelectors = WithSelectors<
  PlayerStore,
  // Exclude currentTime and seekableEnd from selectors because they change too often and cause performance issues
  // You can still access and watch changes using subscribe
  Omit<PlayerStoreProperties, "currentTime" | "seekableEnd">
>;

export const PlayerStoreContext =
  createContext<PlayerStoreWithSelectors | null>(null);
